import type { AccordionListObject } from "@common/components/accordions/AccordionList";

export const securityBestPractices: AccordionListObject[] = [
  {
    id: "strong-passwords",
    title: "Use Strong, Unique Passwords",
    content:
      "Create complex passwords that include uppercase letters, lowercase letters, numbers, and special characters. Each staff member should have a unique password, and passwords should be changed regularly. Never share passwords among staff members.",
  },
  {
    id: "2fa",
    title: "Enable Two-Factor Authentication",
    content:
      "Always use two-factor authentication whenever available. This adds an additional security layer beyond passwords, requiring a second verification method such as a text message code or authentication app.",
  },
  {
    id: "access-controls",
    title: "Implement Role-Based Access Controls",
    content:
      "Assign appropriate access levels to staff members based on their job responsibilities. Front desk staff, hygienists, and dentists should only have access to the information they need to perform their duties.",
  },
  {
    id: "auto-logout",
    title: "Set Automatic Logout Timers",
    content:
      "Configure workstations to automatically log out after periods of inactivity. This prevents unauthorized access if a computer is left unattended.",
  },
  {
    id: "software-updates",
    title: "Keep Software Updated",
    content:
      "Regularly install software updates and security patches as they become available. These updates often contain important security fixes that protect against vulnerabilities.",
  },
  {
    id: "secure-wifi",
    title: "Secure Your Wi-Fi Network",
    content:
      "Use WPA3 encryption for your office Wi-Fi and maintain separate networks for staff and patients. Change default router passwords and regularly update router firmware.",
  },
  {
    id: "device-security",
    title: "Secure All Devices",
    content:
      "Implement encryption and password protection on all devices that access patient data, including computers, tablets, and smartphones. Physical security measures should prevent theft of devices containing protected health information.",
  },
  {
    id: "data-backups",
    title: "Perform Regular Data Backups",
    content:
      "Schedule automated backups of all patient data and verify the integrity of these backups regularly. Store backups securely with encryption, ideally in multiple locations.",
  },
  {
    id: "phishing-awareness",
    title: "Train Staff on Phishing Awareness",
    content:
      "Educate staff about phishing attempts and suspicious emails. Implement protocols for verifying requests for sensitive information and reporting potential security threats.",
  },
  {
    id: "virus-protection",
    title: "Use Antivirus and Anti-malware Protection",
    content:
      "Install and maintain reputable antivirus and anti-malware software on all systems. Schedule regular scans and keep virus definitions updated.",
  },
  {
    id: "hipaa-compliance",
    title: "Maintain HIPAA Compliance",
    content:
      "Regularly review and update your HIPAA policies and procedures. Conduct annual training for all staff members on HIPAA requirements and document this training.",
  },
  {
    id: "encryption",
    title: "Encrypt Sensitive Data",
    content:
      "Use encryption for all patient data, both in transit and at rest. This ensures that even if data is intercepted or stolen, it remains protected.",
  },
  {
    id: "audit-logs",
    title: "Review Audit Logs Regularly",
    content:
      "Monitor system access logs to detect unusual activities or unauthorized access attempts. Establish a process for investigating and addressing security incidents.",
  },
  {
    id: "secure-messaging",
    title: "Use Secure Communication Channels",
    content: "When discussing patient information, always use HIPAA-compliant secure messaging within the software rather than email or text messages.",
  },
  {
    id: "vendor-management",
    title: "Verify Vendor Security Practices",
    content:
      "Ensure all software vendors and third-party services have strong security policies and are HIPAA compliant. Request Business Associate Agreements when appropriate.",
  },
  {
    id: "mobile-device-policy",
    title: "Implement a Mobile Device Policy",
    content:
      "Create clear guidelines for how staff may use personal or office-owned mobile devices to access patient information, including required security measures.",
  },
  {
    id: "screen-privacy",
    title: "Use Privacy Screens and Position Monitors",
    content:
      "Install privacy filters on monitors and position screens away from patient viewing areas to prevent casual observation of protected health information.",
  },
  {
    id: "emergency-plan",
    title: "Develop a Security Breach Response Plan",
    content: "Create a documented procedure for responding to potential security breaches, including notification protocols and steps to mitigate damage.",
  },
  {
    id: "regular-audits",
    title: "Conduct Regular Security Audits",
    content:
      "Perform periodic security assessments to identify and address vulnerabilities in your systems and procedures. Consider professional security audits annually.",
  },
  {
    id: "physical-security",
    title: "Secure Physical Records and Hardware",
    content:
      "Implement physical security measures like locked server rooms, secure disposal of paper records, and controlled access to areas containing sensitive information.",
  },
];

export default securityBestPractices;
